/**
 * Componentes visuais compartilhados pelas páginas do módulo The Consumer Market.
 *
 * O que também é usado por outros módulos mora em `../lifeshub-common`.
 */
import React from 'react'

import { styled } from '@mui/material/styles'

import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'

import { LifesHubCard, LifesHubCardHeader } from '../lifeshub-card'

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Card do dashboard
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

/** Título do card, com espaço para um ícone ou chip ao lado. */
export const Caption = styled('h3')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.2,
    color: 'var(--ink)'
})

/** Contagem exibida abaixo do título do card. */
export const CaptionCount = styled('div')({
    marginTop: 4,
    color: 'var(--green-800)',
    fontWeight: 700,
    fontSize: 12
})

const CardContainer = styled(LifesHubCard)({
    position: 'relative',
    flexGrow: 1
})

const CardBody = styled('div', { shouldForwardProp: (prop) => prop !== 'noHead' && prop !== 'dense' })<{
    noHead?: boolean
    dense?: boolean
}>(({ noHead, dense }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    overflow: 'hidden',
    padding: dense ? '8px 14px 6px' : noHead ? 18 : '0 18px 18px'
}))

export type TcmCardProps = {
    className?: string
    caption?: React.ReactNode
    count?: React.ReactNode
    actions?: React.ReactNode
    bodyClassName?: string
    dense?: boolean
    style?: React.CSSProperties
    children?: React.ReactNode
}

export function TcmCard({ className, caption, count, actions, bodyClassName, dense, style, children }: TcmCardProps) {
    return (
        <CardContainer className={className} style={style} elevation={0}>
            {caption !== undefined && caption !== '' && (
                <LifesHubCardHeader title={caption} count={count} actions={actions} />
            )}
            <CardBody className={bodyClassName} dense={dense} noHead={caption === undefined || caption === ''}>
                {children}
            </CardBody>
        </CardContainer>
    )
}

export default TcmCard

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Filtros
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

/** Bloco que agrupa os campos de um filtro. */
export const FilterPanel = styled('div')({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 12,
    border: '1px solid var(--border)',
    borderRadius: 9,
    backgroundColor: '#fbfcfb'
})

/** Campo do filtro seguido de um botão de ação alinhado pela base. */
export const FieldRow = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 10
})

export const FieldIconButton = styled(IconButton)({
    width: 34,
    height: 34,
    flex: '0 0 auto',
    borderRadius: 8,
    color: '#2d332f',
    '&:hover': { backgroundColor: '#edf1ee' },
    '& .MuiSvgIcon-root': { fontSize: 18 }
})

export const FilterSwitchRow = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '2px 0'
})

export const FilterSwitchLabel = styled('label')({
    display: 'block',
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--ink)',
    cursor: 'pointer'
})

/** Lista de sugestões exibida abaixo do campo de busca de um filtro. */
export const DropdownBody = styled(Paper)({
    maxHeight: 300,
    overflowY: 'auto',
    border: '1px solid var(--border)',
    borderRadius: 10,
    boxShadow: 'var(--shadow-2)'
})

export function criterionLabel(isAnd: boolean) {
    return `Critério (${isAnd ? 'E' : 'OU'})`
}

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Listagens
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

/** Número alinhado à direita dentro de uma célula da listagem. */
export const SpanNumber = styled('span')({
    alignSelf: 'stretch',
    flexGrow: 1,
    textAlign: 'right'
})
